import React, { useState, useEffect, useMemo } from "react";
import PageTableHeader from "../../../../components/pageTableHeader/pageTableHeader";
import CommonTable from "../../../../components/table/table";
import DefaultButton from "../../../../components/buttons/defaultButton";
import DefaultSearchSelectInput from "../../../../components/inputs/defaultSearchSelectInput";
import DefaultDatePicker from "../../../../components/inputs/defaultDatePicker";
import DefaultBadge from "../../../../components/badge/defaultBadge";
import { Modal } from "flowbite-react";
import { 
  HiOutlineClock, 
  HiOutlineUserGroup, 
  HiOutlineXCircle,
  HiOutlineCheckCircle,
  HiOutlineExclamationCircle,
  HiOutlineDownload,
  HiOutlineRefresh,
  HiOutlineInbox
} from "react-icons/hi";
import { IoMdAdd } from "react-icons/io";
import moment from "moment";
import toast from "react-hot-toast";
import { getManagerTeamAttendance } from "../../../../../services/workspace/attendanceManagement.service";
import { getLeaveList, updateLeave, updateLeavePost } from "../../../../../services/workspace/leave.service";
import { getUserDetailProfile } from "../../../../../services/workspace/user.service";
import DefaultTextarea from "../../../../components/inputs/defaultTextarea";
import DefaultCheckbox from "../../../../components/inputs/defaultCheckbox";
import { useSelector } from "react-redux";

const items = [{ label: "Manager Dashboard", path: "" }];

const ManagerDashboard = () => {
  const user = useSelector((state) => state?.user?.userInfo?.user || state?.user?.user);
  const [selectedDate, setSelectedDate] = useState(moment().format("YYYY-MM-DD"));
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [selectedShift, setSelectedShift] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [attendanceData, setAttendanceData] = useState(null);
  const [selectedWidget, setSelectedWidget] = useState(null);
  const [isCorrectionModalOpen, setIsCorrectionModalOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [pendingLeaves, setPendingLeaves] = useState([]);
  const [loadingLeaves, setLoadingLeaves] = useState(false);
  const [selectedLeaves, setSelectedLeaves] = useState([]);
  const [bulkActionType, setBulkActionType] = useState(null); // 'approve' or 'reject'
  const [bulkRejectionNotes, setBulkRejectionNotes] = useState("");
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
  const [isLeaveDetailModalOpen, setIsLeaveDetailModalOpen] = useState(false);
  const [selectedLeaveDetail, setSelectedLeaveDetail] = useState(null);
  const [rejectionNotes, setRejectionNotes] = useState("");
  const [managerProfile, setManagerProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [teams, setTeams] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [teamLeaders, setTeamLeaders] = useState([]);

  // Get manager ID and name from user
  const managerId = user?.uuid || user?.id;
  const managerName = user?.name || "Manager";

  // Fetch manager profile with teams and members
  useEffect(() => {
    if (managerId && user?.is_team_manager) {
      fetchManagerProfile();
    }
  }, [managerId, user?.is_team_manager]);

  useEffect(() => {
    if (managerProfile) {
      fetchAttendanceData();
      fetchPendingLeaves();
    }
  }, [selectedDate, selectedTeam, selectedShift, managerProfile]);

  // Fetch manager profile with teams, team leaders, and team members
  const fetchManagerProfile = async () => {
    if (!managerId) return;
    
    setLoadingProfile(true);
    try {
      const response = await getUserDetailProfile(managerId, {
        with: "teamMember,teamLeader,manageTeams",
      });
      
      const profileData = response?.user || response;
      setManagerProfile(profileData);
      
      // Extract teams, team members, and team leaders
      const manageTeams = profileData?.manageTeams || profileData?.manage_teams || [];
      const teamMemberList = profileData?.teamMember || profileData?.team_member || [];
      const teamLeaderList = profileData?.teamLeader || profileData?.team_leader || [];
      
      setTeams(manageTeams);
      setTeamMembers(teamMemberList);
      setTeamLeaders(teamLeaderList);
      
      // Set default team if available
      if (manageTeams.length > 0 && !selectedTeam) {
        setSelectedTeam({
          value: manageTeams[0].uuid || manageTeams[0].id,
          label: manageTeams[0].name || "Team",
        });
      }
    } catch (error) {
      toast.error("Failed to fetch manager profile");
      console.error(error);
    } finally {
      setLoadingProfile(false);
    }
  };

  // Fetch pending leaves for approval (only for manager's team members)
  const fetchPendingLeaves = async () => {
    if (!managerId) return;
    
    setLoadingLeaves(true);
    try {
      // Get team member IDs
      const teamMemberIds = teamMembers.map(member => member.uuid || member.id);
      
      const response = await getLeaveList({
        status: 0, // Pending status
        per_page: 100,
        // Filter by team member IDs if available
        ...(teamMemberIds.length > 0 && { user_ids: teamMemberIds }),
      });
      setPendingLeaves(response?.leave?.data || []);
    } catch (error) {
      toast.error("Failed to fetch pending leaves");
      console.error(error);
    } finally {
      setLoadingLeaves(false);
    }
  };

  const fetchAttendanceData = async () => {
    if (!managerId) return;
    
    setLoading(true);
    try {
      // Get team member IDs for attendance
      const teamMemberIds = teamMembers.map(member => member.uuid || member.id);
      
      const response = await getManagerTeamAttendance({
        manager_id: managerId,
        date: selectedDate,
        team_id: selectedTeam?.value,
        shift_id: selectedShift?.value,
        // Pass team member IDs if available
        ...(teamMemberIds.length > 0 && { employee_ids: teamMemberIds }),
      });
      setAttendanceData(response);
    } catch (error) {
      toast.error("Failed to fetch attendance data");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Calculate summary statistics
  const summaryStats = useMemo(() => {
    if (!attendanceData?.team) return null;

    const team = attendanceData.team;
    const total = team.length;
    const present = team.filter(e => e.status === "present" || e.status === "Present").length;
    const absent = team.filter(e => e.status === "absent" || e.status === "Absent").length;
    const onLeave = team.filter(e => e.status === "on_leave" || e.status === "On Leave").length;
    const late = team.filter(e => e.late_by && e.late_by > 0).length;
    const exceptions = team.filter(e => e.exception_type).length;

    return { total, present, absent, onLeave, late, exceptions };
  }, [attendanceData]);

  // Filter data based on selected widget
  const filteredData = useMemo(() => {
    if (!attendanceData?.team) return [];

    let filtered = [...attendanceData.team];

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(emp => 
        emp.name?.toLowerCase().includes(query) ||
        emp.employee_id?.toLowerCase().includes(query)
      );
    }

    // Apply widget filter
    if (selectedWidget === "late") {
      filtered = filtered.filter(e => e.late_by && e.late_by > 0);
    } else if (selectedWidget === "absent") {
      filtered = filtered.filter(e => e.status === "absent" || e.status === "Absent");
    } else if (selectedWidget === "exceptions") {
      filtered = filtered.filter(e => e.exception_type);
    } else if (selectedWidget === "present") {
      filtered = filtered.filter(e => e.status === "present" || e.status === "Present");
    } else if (selectedWidget === "on_leave") {
      filtered = filtered.filter(e => e.status === "on_leave" || e.status === "On Leave");
    }

    return filtered;
  }, [attendanceData, selectedWidget, searchQuery]);

  // Late List Table Configuration
  const lateListHeaders = [
    { label: "Employee", key: "employee" },
    { label: "Shift", key: "shift" },
    { label: "Scheduled Start", key: "scheduled_start" },
    { label: "Check-in Time", key: "checkin_time" },
    { label: "Late By", key: "late_by" },
    { label: "Reason", key: "reason" },
    { label: "Actions", key: "actions" },
  ];

  // Absent List Table Configuration
  const absentListHeaders = [
    { label: "Employee", key: "employee" },
    { label: "Shift", key: "shift" },
    { label: "Last Seen", key: "last_seen" },
    { label: "Absent Since", key: "absent_since" },
    { label: "Actions", key: "actions" },
  ];

  // Transform data for table
  const transformTableData = (data, type) => {
    return data.map((item) => {
      const baseRow = {
        employee: {
          data: (
            <div className="flex items-center gap-2">
              {item.photo && (
                <img 
                  src={item.photo} 
                  alt={item.name}
                  className="w-8 h-8 rounded-full object-cover"
                />
              )}
              <div className="flex flex-col">
                <span className="font-medium text-sm text-theme">{item.name}</span>
                {item.employee_id && (
                  <span className="text-xs text-textLight">{item.employee_id}</span>
                )}
              </div>
            </div>
          ),
          variant: "custom",
        },
        shift: {
          data: item.shift_name || "-",
          variant: "text",
        },
      };

      if (type === "late") {
        return {
          ...baseRow,
          scheduled_start: {
            data: item.scheduled_start_time || "-",
            variant: "text",
          },
          checkin_time: {
            data: item.checkin || "-",
            variant: "text",
          },
          late_by: {
            data: item.late_by ? formatLateBy(item.late_by) : "-",
            variant: "text",
            className: "text-error-600 font-medium",
          },
          reason: {
            data: item.late_reason || "-",
            variant: "text",
          },
          actions: {
            data: item.employee_id,
            variant: "custom",
            customRender: () => (
              <div className="flex gap-2">
                <DefaultButton
                  name="Mark Excuse"
                  size="sm"
                  variant="outline"
                  onClick={() => handleMarkExcuse(item)}
                />
                <DefaultButton
                  name="Add Note"
                  size="sm"
                  variant="secondary"
                  onClick={() => handleAddNote(item)}
                />
              </div>
            ),
          },
        };
      } else if (type === "absent") {
        return {
          ...baseRow,
          last_seen: {
            data: item.last_seen ? moment(item.last_seen).format("MMM DD, YYYY") : "-",
            variant: "text",
          },
          absent_since: {
            data: item.absent_since ? moment(item.absent_since).format("MMM DD, YYYY") : "-",
            variant: "text",
          },
          actions: {
            data: item.employee_id,
            variant: "custom",
            customRender: () => (
              <div className="flex gap-2">
                <DefaultButton
                  name="Mark Remote"
                  size="sm"
                  variant="outline"
                  onClick={() => handleMarkRemote(item)}
                />
                <DefaultButton
                  name="Mark On Leave"
                  size="sm"
                  variant="secondary"
                  onClick={() => handleMarkOnLeave(item)}
                />
              </div>
            ),
          },
        };
      }

      return baseRow;
    });
  };

  const formatLateBy = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, "0")}:${mins.toString().padStart(2, "0")}`;
  };

  const handleMarkExcuse = (employee) => {
    setSelectedEmployee(employee);
    setIsCorrectionModalOpen(true);
  };

  const handleAddNote = (employee) => {
    // Open note modal
    toast.info(`Add note for ${employee.name}`);
  };

  const handleMarkRemote = (employee) => {
    toast.info(`Mark ${employee.name} as remote`);
  };

  const handleMarkOnLeave = (employee) => {
    toast.info(`Mark ${employee.name} on leave`);
  };

  const handleWidgetClick = (widgetType) => {
    setSelectedWidget(selectedWidget === widgetType ? null : widgetType);
  };

  const handleExportToday = () => {
    toast.success("Exporting today's attendance...");
  };

  const handleMassCorrection = () => {
    toast.info("Opening mass correction modal...");
  };

  // Leave approval handlers (following existing system)
  const handleApprove = async (leaveId, payload = {}) => {
    try {
      const payload2 = {
        leave_id: leaveId,
        ...payload,
      };
      await updateLeavePost(payload2);
      toast.success("Leave approved successfully!");
      fetchPendingLeaves();
      setIsLeaveDetailModalOpen(false);
    } catch (error) {
      toast.error("Failed to approve leave");
      console.error(error);
    }
  };

  const handleReject = async (leaveId, notes = "") => {
    try {
      const payload = {
        status: 2,
        remarks: notes,
      };
      await updateLeave(leaveId, payload);
      toast.success("Leave rejected successfully!");
      fetchPendingLeaves();
      setIsLeaveDetailModalOpen(false);
      setRejectionNotes("");
    } catch (error) {
      toast.error("Failed to reject leave");
      console.error(error);
    }
  };

  // Bulk approval/rejection handlers (following existing system)
  const handleBulkApprove = async () => {
    if (selectedLeaves.length === 0) {
      toast.error("Please select at least one leave to approve");
      return;
    }

    try {
      const promises = selectedLeaves.map((leave) => {
        const payload = {
          leave_id: leave.uuid || leave.id,
        };
        return updateLeavePost(payload);
      });
      await Promise.all(promises);
      toast.success(`${selectedLeaves.length} leave(s) approved successfully!`);
      setSelectedLeaves([]);
      setBulkActionType(null);
      setIsBulkModalOpen(false);
      fetchPendingLeaves();
    } catch (error) {
      toast.error("Failed to approve some leaves");
      console.error(error);
    }
  };

  const handleBulkReject = async () => {
    if (selectedLeaves.length === 0) {
      toast.error("Please select at least one leave to reject");
      return;
    }

    if (!bulkRejectionNotes.trim()) {
      toast.error("Please provide rejection notes");
      return;
    }

    try {
      const promises = selectedLeaves.map((leave) => {
        const payload = {
          status: 2,
          remarks: bulkRejectionNotes,
        };
        return updateLeave(leave.uuid || leave.id, payload);
      });
      await Promise.all(promises);
      toast.success(`${selectedLeaves.length} leave(s) rejected successfully!`);
      setSelectedLeaves([]);
      setBulkActionType(null);
      setBulkRejectionNotes("");
      setIsBulkModalOpen(false);
      fetchPendingLeaves();
    } catch (error) {
      toast.error("Failed to reject some leaves");
      console.error(error);
    }
  };

  const handleSelectLeave = (leave) => {
    setSelectedLeaves((prev) => {
      const exists = prev.find((l) => (l.uuid || l.id) === (leave.uuid || leave.id));
      if (exists) {
        return prev.filter((l) => (l.uuid || l.id) !== (leave.uuid || leave.id));
      }
      return [...prev, leave];
    });
  };

  const handleSelectAllLeaves = () => {
    if (selectedLeaves.length === pendingLeaves.length) {
      setSelectedLeaves([]);
    } else {
      setSelectedLeaves([...pendingLeaves]);
    }
  };

  // Dynamic team options from manager profile
  const teamOptions = useMemo(() => {
    return teams.map((team) => ({
      value: team.uuid || team.id,
      label: team.name || "Team",
    }));
  }, [teams]);

  // Shift options - can be fetched from shifts API if needed
  const shiftOptions = [
    { value: "shift_001", label: "Morning Shift (9 AM - 6 PM)" },
    { value: "shift_002", label: "Evening Shift (2 PM - 11 PM)" },
    { value: "shift_003", label: "Night Shift (10 PM - 7 AM)" },
  ];

  // Check if user is team manager
  if (!user?.is_team_manager && user?.is_team_manager !== 1) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
        <HiOutlineExclamationCircle className="w-16 h-16 text-gray-400" />
        <h2 className="text-xl font-semibold text-theme">Access Restricted</h2>
        <p className="text-sm text-textLight text-center max-w-md">
          You don't have manager access. This dashboard is only available for team managers.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <PageTableHeader
        breadcrumbs={items}
        isSearch
        onSearchChange={setSearchQuery}
        buttons={
          <div className="flex gap-2">
            <DefaultDatePicker
              label=""
              value={selectedDate}
              onChange={(date) => setSelectedDate(moment(date).format("YYYY-MM-DD"))}
              classnames="w-40"
            />
            <DefaultSearchSelectInput
              label=""
              options={teamOptions}
              value={selectedTeam}
              onChange={setSelectedTeam}
              placeholder={loadingProfile ? "Loading teams..." : teams.length > 0 ? "Select Team" : "No Teams"}
              isMulti={false}
              customWidth="w-48"
              isDisabled={loadingProfile || teams.length === 0}
            />
            <DefaultSearchSelectInput
              label=""
              options={shiftOptions}
              value={selectedShift}
              onChange={setSelectedShift}
              placeholder="All Shifts"
              isMulti={false}
              customWidth="w-48"
            />
          </div>
        }
      />

      {/* Page Header */}
      <div className="bg-white p-4 rounded-md shadow-sm">
        <h1 className="text-2xl font-semibold text-theme">
          Manager Dashboard — {managerName}
        </h1>
        <p className="text-sm text-textLight mt-1">
          View and manage your team's attendance for {moment(selectedDate).format("MMMM DD, YYYY")}
        </p>
        {loadingProfile ? (
          <p className="text-xs text-textLight mt-1">Loading team information...</p>
        ) : teams.length > 0 ? (
          <p className="text-xs text-textLight mt-1">
            Managing {teams.length} team(s) with {teamMembers.length} team member(s)
          </p>
        ) : (
          <p className="text-xs text-warning-600 mt-1">
            No teams assigned. Please contact HR to assign teams.
          </p>
        )}
      </div>

      {/* Summary Widgets */}
      {summaryStats && (
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-7 gap-4">
          <WidgetCard
            title="Pending Approvals"
            value={pendingLeaves.length}
            icon={<HiOutlineInbox />}
            color="bg-indigo-50 text-indigo-600"
            onClick={() => {
              // Scroll to approval inbox
              const inboxElement = document.getElementById("approval-inbox");
              if (inboxElement) {
                inboxElement.scrollIntoView({ behavior: "smooth", block: "start" });
              }
            }}
            isActive={false}
          />
          <WidgetCard
            title="Total Team"
            value={summaryStats.total}
            icon={<HiOutlineUserGroup />}
            color="bg-blue-50 text-blue-600"
            onClick={() => handleWidgetClick("all")}
            isActive={selectedWidget === "all"}
          />
          <WidgetCard
            title="Present"
            value={summaryStats.present}
            icon={<HiOutlineCheckCircle />}
            color="bg-green-50 text-green-600"
            onClick={() => handleWidgetClick("present")}
            isActive={selectedWidget === "present"}
          />
          <WidgetCard
            title="Absent"
            value={summaryStats.absent}
            icon={<HiOutlineXCircle />}
            color="bg-red-50 text-red-600"
            onClick={() => handleWidgetClick("absent")}
            isActive={selectedWidget === "absent"}
          />
          <WidgetCard
            title="On Leave"
            value={summaryStats.onLeave}
            icon={<HiOutlineUserGroup />}
            color="bg-yellow-50 text-yellow-600"
            onClick={() => handleWidgetClick("on_leave")}
            isActive={selectedWidget === "on_leave"}
          />
          <WidgetCard
            title="Late"
            value={summaryStats.late}
            icon={<HiOutlineClock />}
            color="bg-orange-50 text-orange-600"
            onClick={() => handleWidgetClick("late")}
            isActive={selectedWidget === "late"}
          />
          <WidgetCard
            title="Exceptions"
            value={summaryStats.exceptions}
            icon={<HiOutlineExclamationCircle />}
            color="bg-purple-50 text-purple-600"
            onClick={() => handleWidgetClick("exceptions")}
            isActive={selectedWidget === "exceptions"}
          />
        </div>
      )}

      {/* Approval Inbox */}
      {pendingLeaves.length > 0 && (
        <div id="approval-inbox" className="bg-white rounded-md shadow-sm">
          <div className="p-4 border-b border-borderColor">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-theme flex items-center gap-2">
                  <HiOutlineInbox className="w-5 h-5 text-primary-600" />
                  Approval Inbox
                </h3>
                <p className="text-sm text-textLight mt-1">
                  {pendingLeaves.length} pending leave request(s) awaiting your approval
                </p>
              </div>
              {selectedLeaves.length > 0 && (
                <div className="flex gap-2">
                  <DefaultButton
                    name={`Approve Selected (${selectedLeaves.length})`}
                    variant="success"
                    size="sm"
                    onClick={() => {
                      setBulkActionType("approve");
                      setIsBulkModalOpen(true);
                    }}
                  />
                  <DefaultButton
                    name={`Reject Selected (${selectedLeaves.length})`}
                    variant="danger"
                    size="sm"
                    onClick={() => {
                      setBulkActionType("reject");
                      setIsBulkModalOpen(true);
                    }}
                  />
                </div>
              )}
            </div>
          </div>
          <CommonTable
            TableHeaderData={[
              { label: "", key: "select" },
              { label: "Employee", key: "employee" },
              { label: "Leave Type", key: "leave_type" },
              { label: "From Date", key: "start_date" },
              { label: "To Date", key: "end_date" },
              { label: "Total Days", key: "total_days" },
              { label: "Reason", key: "reason" },
              { label: "Actions", key: "actions" },
            ]}
            tabledata={pendingLeaves.map((leave) => {
              const isSelected = selectedLeaves.some(
                (l) => (l.uuid || l.id) === (leave.uuid || leave.id)
              );
              return {
                select: {
                  data: leave.uuid || leave.id,
                  variant: "custom",
                  customRender: () => (
                    <DefaultCheckbox
                      value={isSelected}
                      onChange={() => handleSelectLeave(leave)}
                    />
                  ),
                },
                employee: {
                  data: (
                    <div className="flex items-center gap-2">
                      {leave.user?.photo && (
                        <img
                          src={leave.user.photo}
                          alt={leave.user.name}
                          className="w-8 h-8 rounded-full object-cover"
                        />
                      )}
                      <div className="flex flex-col">
                        <span className="font-medium text-sm text-theme">
                          {leave.user?.name || leave.name || "Unknown"}
                        </span>
                        {leave.user?.code && (
                          <span className="text-xs text-textLight">{leave.user.code}</span>
                        )}
                      </div>
                    </div>
                  ),
                  variant: "custom",
                },
                leave_type: {
                  data: leave.leave_type?.name || leave.leaveType?.name || "-",
                  variant: "text",
                },
                start_date: {
                  data: leave.start_date
                    ? moment(leave.start_date).format("MMM DD, YYYY")
                    : "-",
                  variant: "text",
                },
                end_date: {
                  data: leave.end_date
                    ? moment(leave.end_date).format("MMM DD, YYYY")
                    : "-",
                  variant: "text",
                },
                total_days: {
                  data: leave.total_days || leave.totalDays || "-",
                  variant: "text",
                },
                reason: {
                  data: leave.reason || "-",
                  variant: "text",
                },
                actions: {
                  data: leave.uuid || leave.id,
                  variant: "custom",
                  customRender: () => (
                    <div className="flex gap-2">
                      <DefaultButton
                        name="View"
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setSelectedLeaveDetail(leave);
                          setIsLeaveDetailModalOpen(true);
                        }}
                      />
                      <DefaultButton
                        name="Approve"
                        size="sm"
                        variant="success"
                        onClick={() => handleApprove(leave.uuid || leave.id)}
                      />
                      <DefaultButton
                        name="Reject"
                        size="sm"
                        variant="danger"
                        onClick={() => {
                          setSelectedLeaveDetail(leave);
                          setRejectionNotes("");
                          setIsLeaveDetailModalOpen(true);
                        }}
                      />
                    </div>
                  ),
                },
              };
            })}
            loading={loadingLeaves}
            showActions={false}
          />
          {pendingLeaves.length > 0 && (
            <div className="p-4 border-t border-borderColor bg-gray-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <DefaultCheckbox
                    value={selectedLeaves.length === pendingLeaves.length && pendingLeaves.length > 0}
                    onChange={handleSelectAllLeaves}
                  />
                  <span className="text-sm text-textLight">
                    Select All ({selectedLeaves.length}/{pendingLeaves.length} selected)
                  </span>
                </div>
                {selectedLeaves.length > 0 && (
                  <div className="flex gap-2">
                    <DefaultButton
                      name="Bulk Approve"
                      variant="success"
                      size="sm"
                      onClick={() => {
                        setBulkActionType("approve");
                        setIsBulkModalOpen(true);
                      }}
                    />
                    <DefaultButton
                      name="Bulk Reject"
                      variant="danger"
                      size="sm"
                      onClick={() => {
                        setBulkActionType("reject");
                        setIsBulkModalOpen(true);
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Quick Actions */}
      <div className="bg-white p-4 rounded-md shadow-sm">
        <h3 className="text-lg font-semibold text-theme mb-3">Quick Actions</h3>
        <div className="flex gap-2 flex-wrap">
          <DefaultButton
            name="Apply Mass Corrections"
            variant="primary"
            icon={<IoMdAdd />}
            onClick={handleMassCorrection}
          />
          <DefaultButton
            name="Open HR Shift Editor"
            variant="outline"
            onClick={() => window.location.href = "/hr/shift-editor"}
          />
          <DefaultButton
            name="Export Today's Attendance"
            variant="outline"
            icon={<HiOutlineDownload />}
            onClick={handleExportToday}
          />
          <DefaultButton
            name="Refresh"
            variant="secondary"
            icon={<HiOutlineRefresh />}
            onClick={() => {
              fetchAttendanceData();
              fetchPendingLeaves();
            }}
          />
        </div>
      </div>

      {/* Late List */}
      {selectedWidget === "late" && (
        <div className="bg-white rounded-md shadow-sm">
          <div className="p-4 border-b border-borderColor">
            <h3 className="text-lg font-semibold text-theme">Late List</h3>
            <p className="text-sm text-textLight">
              Employees who checked in late today
            </p>
          </div>
          <CommonTable
            TableHeaderData={lateListHeaders}
            tabledata={transformTableData(filteredData, "late").map((item, idx) => ({
              ...item,
              id: { data: filteredData[idx]?.employee_id || idx, variant: "text" },
            }))}
            loading={loading}
            showActions={false}
          />
        </div>
      )}

      {/* Absent List */}
      {selectedWidget === "absent" && (
        <div className="bg-white rounded-md shadow-sm">
          <div className="p-4 border-b border-borderColor">
            <h3 className="text-lg font-semibold text-theme">Absent List</h3>
            <p className="text-sm text-textLight">
              Employees who are absent today
            </p>
          </div>
          <CommonTable
            TableHeaderData={absentListHeaders}
            tabledata={transformTableData(filteredData, "absent").map((item, idx) => ({
              ...item,
              id: { data: filteredData[idx]?.employee_id || idx, variant: "text" },
            }))}
            loading={loading}
            showActions={false}
          />
        </div>
      )}

      {/* Exceptions List */}
      {selectedWidget === "exceptions" && (
        <div className="bg-white rounded-md shadow-sm">
          <div className="p-4 border-b border-borderColor">
            <h3 className="text-lg font-semibold text-theme">Exceptions</h3>
            <p className="text-sm text-textLight">
              Missing check-outs, multiple check-ins, geofence violations
            </p>
          </div>
          <CommonTable
            TableHeaderData={[
              { label: "Employee", key: "employee" },
              { label: "Exception Type", key: "exception_type" },
              { label: "Details", key: "details" },
              { label: "Time", key: "time" },
              { label: "Actions", key: "actions" },
            ]}
            tabledata={filteredData.map(item => ({
              employee: {
                data: item.name,
                variant: "text",
              },
              exception_type: {
                data: item.exception_type,
                variant: "badge",
                customCases: {
                  "missing_checkout": "Missing Check-out",
                  "multiple_checkin": "Multiple Check-in",
                  "geofence_violation": "Geofence Violation",
                },
              },
              details: {
                data: item.exception_details || "-",
                variant: "text",
              },
              time: {
                data: item.exception_time || "-",
                variant: "text",
              },
              actions: {
                data: item.employee_id,
                variant: "custom",
                customRender: () => (
                  <DefaultButton
                    name="Resolve"
                    size="sm"
                    variant="outline"
                    onClick={() => toast.info(`Resolve exception for ${item.name}`)}
                  />
                ),
              },
            }))}
            loading={loading}
            showActions={false}
          />
        </div>
      )}

      {/* All Team View */}
      {!selectedWidget && (
        <div className="bg-white rounded-md shadow-sm">
          <div className="p-4 border-b border-borderColor">
            <h3 className="text-lg font-semibold text-theme">Today's Attendance</h3>
            <p className="text-sm text-textLight">
              Complete attendance overview for your team
            </p>
          </div>
          <CommonTable
            TableHeaderData={[
              { label: "Employee", key: "employee" },
              { label: "Shift", key: "shift" },
              { label: "Check-in", key: "checkin" },
              { label: "Check-out", key: "checkout" },
              { label: "Work Hours", key: "work_hours" },
              { label: "Status", key: "status" },
              { label: "Full Day", key: "full_day" },
            ]}
            tabledata={filteredData.map(item => ({
              employee: {
                data: item.name,
                variant: "text",
              },
              shift: {
                data: item.shift_name || "-",
                variant: "text",
              },
              checkin: {
                data: item.checkin || "-",
                variant: "text",
              },
              checkout: {
                data: item.checkout || "-",
                variant: "text",
              },
              work_hours: {
                data: item.work_hours ? `${item.work_hours.toFixed(2)}h` : "-",
                variant: "text",
              },
              status: {
                data: item.status,
                variant: "status",
                customCases: {
                  "Present": "Present",
                  "Absent": "Absent",
                  "On Leave": "On Leave",
                  "Late": "Late",
                },
                background: item.status === "Present" ? "bg-success-50" : 
                           item.status === "Absent" ? "bg-error-50" : 
                           item.status === "On Leave" ? "bg-warning-50" : "bg-primary-50",
                text: item.status === "Present" ? "text-success-700" : 
                      item.status === "Absent" ? "text-error-700" : 
                      item.status === "On Leave" ? "text-warning-700" : "text-primary-700",
              },
              full_day: {
                data: item.full_day ? "Yes" : "No",
                variant: "badge",
                customCases: {
                  "Yes": "Yes",
                  "No": "No",
                },
                background: item.full_day ? "bg-success-50" : "bg-gray-100",
                text: item.full_day ? "text-success-700" : "text-gray-700",
              },
            }))}
            loading={loading}
            showActions={false}
          />
        </div>
      )}

      {/* Correction Modal */}
      <Modal show={isCorrectionModalOpen} onClose={() => setIsCorrectionModalOpen(false)}>
        <Modal.Header>Mark Excuse for {selectedEmployee?.name}</Modal.Header>
        <Modal.Body>
          <div className="flex flex-col gap-4">
            <p className="text-sm text-textLight">
              Add a reason for the late arrival or absence.
            </p>
            {/* Add form fields here */}
            <div className="flex gap-2 justify-end">
              <DefaultButton
                name="Cancel"
                variant="secondary"
                onClick={() => setIsCorrectionModalOpen(false)}
              />
              <DefaultButton
                name="Save"
                variant="primary"
                onClick={() => {
                  toast.success("Excuse marked successfully");
                  setIsCorrectionModalOpen(false);
                  fetchAttendanceData();
                }}
              />
            </div>
          </div>
        </Modal.Body>
      </Modal>

      {/* Bulk Action Modal */}
      <Modal
        show={isBulkModalOpen}
        onClose={() => {
          setIsBulkModalOpen(false);
          setBulkActionType(null);
          setBulkRejectionNotes("");
        }}
      >
        <Modal.Header>
          {bulkActionType === "approve"
            ? `Approve ${selectedLeaves.length} Leave(s)`
            : `Reject ${selectedLeaves.length} Leave(s)`}
        </Modal.Header>
        <Modal.Body>
          <div className="flex flex-col gap-4">
            {bulkActionType === "approve" ? (
              <>
                <p className="text-sm text-textLight">
                  Are you sure you want to approve {selectedLeaves.length} leave request(s)?
                </p>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm text-blue-800">
                    <strong>Selected Leaves:</strong>
                  </p>
                  <ul className="text-xs text-blue-700 mt-2 space-y-1">
                    {selectedLeaves.slice(0, 5).map((leave, idx) => (
                      <li key={idx}>
                        • {leave.user?.name || leave.name} - {leave.leave_type?.name || leave.leaveType?.name} ({leave.total_days || leave.totalDays} days)
                      </li>
                    ))}
                    {selectedLeaves.length > 5 && (
                      <li className="text-blue-600">... and {selectedLeaves.length - 5} more</li>
                    )}
                  </ul>
                </div>
              </>
            ) : (
              <>
                <p className="text-sm text-textLight">
                  Please provide rejection notes for {selectedLeaves.length} leave request(s):
                </p>
                <DefaultTextarea
                  label="Rejection Notes"
                  value={bulkRejectionNotes}
                  onChange={(e) => setBulkRejectionNotes(e.target.value)}
                  placeholder="Enter rejection reason..."
                  required
                />
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm text-red-800">
                    <strong>Selected Leaves:</strong>
                  </p>
                  <ul className="text-xs text-red-700 mt-2 space-y-1">
                    {selectedLeaves.slice(0, 5).map((leave, idx) => (
                      <li key={idx}>
                        • {leave.user?.name || leave.name} - {leave.leave_type?.name || leave.leaveType?.name} ({leave.total_days || leave.totalDays} days)
                      </li>
                    ))}
                    {selectedLeaves.length > 5 && (
                      <li className="text-red-600">... and {selectedLeaves.length - 5} more</li>
                    )}
                  </ul>
                </div>
              </>
            )}
            <div className="flex gap-2 justify-end">
              <DefaultButton
                name="Cancel"
                variant="secondary"
                onClick={() => {
                  setIsBulkModalOpen(false);
                  setBulkActionType(null);
                  setBulkRejectionNotes("");
                }}
              />
              <DefaultButton
                name={bulkActionType === "approve" ? "Approve All" : "Reject All"}
                variant={bulkActionType === "approve" ? "success" : "danger"}
                onClick={() => {
                  if (bulkActionType === "approve") {
                    handleBulkApprove();
                  } else {
                    handleBulkReject();
                  }
                }}
                disabled={bulkActionType === "reject" && !bulkRejectionNotes.trim()}
              />
            </div>
          </div>
        </Modal.Body>
      </Modal>

      {/* Individual Leave Detail/Reject Modal */}
      <Modal
        show={isLeaveDetailModalOpen}
        onClose={() => {
          setIsLeaveDetailModalOpen(false);
          setSelectedLeaveDetail(null);
          setRejectionNotes("");
        }}
      >
        <Modal.Header>
          {selectedLeaveDetail && (
            <>
              Leave Request - {selectedLeaveDetail.user?.name || selectedLeaveDetail.name}
            </>
          )}
        </Modal.Header>
        <Modal.Body>
          {selectedLeaveDetail && (
            <div className="flex flex-col gap-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-textLight">Leave Type</p>
                  <p className="text-sm font-medium">
                    {selectedLeaveDetail.leave_type?.name || selectedLeaveDetail.leaveType?.name || "-"}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-textLight">Total Days</p>
                  <p className="text-sm font-medium">
                    {selectedLeaveDetail.total_days || selectedLeaveDetail.totalDays || "-"} days
                  </p>
                </div>
                <div>
                  <p className="text-xs text-textLight">From Date</p>
                  <p className="text-sm font-medium">
                    {selectedLeaveDetail.start_date
                      ? moment(selectedLeaveDetail.start_date).format("MMM DD, YYYY")
                      : "-"}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-textLight">To Date</p>
                  <p className="text-sm font-medium">
                    {selectedLeaveDetail.end_date
                      ? moment(selectedLeaveDetail.end_date).format("MMM DD, YYYY")
                      : "-"}
                  </p>
                </div>
                <div className="col-span-2">
                  <p className="text-xs text-textLight">Reason</p>
                  <p className="text-sm font-medium">
                    {selectedLeaveDetail.reason || "-"}
                  </p>
                </div>
              </div>

              <div className="border-t border-borderColor pt-4">
                <DefaultTextarea
                  label="Rejection Notes (if rejecting)"
                  value={rejectionNotes}
                  onChange={(e) => setRejectionNotes(e.target.value)}
                  placeholder="Enter rejection reason..."
                />
              </div>

              <div className="flex gap-2 justify-end">
                <DefaultButton
                  name="Cancel"
                  variant="secondary"
                  onClick={() => {
                    setIsLeaveDetailModalOpen(false);
                    setSelectedLeaveDetail(null);
                    setRejectionNotes("");
                  }}
                />
                <DefaultButton
                  name="Approve"
                  variant="success"
                  onClick={() => handleApprove(selectedLeaveDetail.uuid || selectedLeaveDetail.id)}
                />
                <DefaultButton
                  name="Reject"
                  variant="danger"
                  onClick={() => handleReject(selectedLeaveDetail.uuid || selectedLeaveDetail.id, rejectionNotes)}
                  disabled={!rejectionNotes.trim()}
                />
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

// Widget Card Component
const WidgetCard = ({ title, value, icon, color, onClick, isActive }) => {
  return (
    <div
      className={`bg-white p-4 rounded-md shadow-sm border-2 cursor-pointer transition-all ${
        isActive ? "border-theme" : "border-transparent hover:border-borderColor"
      }`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-textLight mb-1">{title}</p>
          <p className="text-2xl font-bold text-theme">{value}</p>
        </div>
        <div className={`p-3 rounded-full ${color}`}>
          {icon}
        </div>
      </div>
    </div>
  );
};

export default ManagerDashboard;

